<template>
  <div
    v-if="isFinishCreated"
    class="pega-preview"
    data-test="matching"
  >
    <h6>■マッチング条件設定</h6>
    <div class="pega-comment-block">
      <preview-job-comments
        :comments="profileCommentHighlights"
        :is-show="isShowComment"
        data-test="profile-comments"
        v-on="$listeners"
      />
      <table
        class="unstriped hover pega-table pega-preview-table"
        :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
        data-test="matching-setting-profile"
      >
        <thead>
          <tr>
            <th colspan="2">
              個人プロフィール
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="matchingSetting.isStopMatching != null">
            <th>マッチング条件</th>
            <td
              :id="targetIdMap.profile.matchingSettingStatus"
              class="boss-table-data"
              data-test="matching-setting-status"
            >
              マッチング{{ matchingSettingStatusLabel }}
            </td>
          </tr>
          <tr v-if="matchingSetting.gender">
            <th>性別</th>
            <td
              :id="targetIdMap.profile.gender"
              class="boss-table-data"
              data-test="gender"
            >
              {{ matchingSetting.gender }}
            </td>
          </tr>
          <tr v-if="matchingSetting.age.min || matchingSetting.age.max">
            <th>年齢</th>
            <td
              :id="targetIdMap.profile.age"
              class="boss-table-data"
              data-test="age"
            >
              {{ matchingSetting.age.min }}歳 ~ {{ matchingSetting.age.max }}歳
            </td>
          </tr>
          <tr v-if="matchingSetting.matchingPlaces.length > 0">
            <th>現住所</th>
            <td
              :id="targetIdMap.profile.matchingPlaces"
              class="boss-table-data"
              data-test="matching-places"
            >
              {{ matchingSetting.matchingPlaces | joinArray }}
            </td>
          </tr>
          <tr v-if="matchingSetting.educationLevel">
            <th>最終学歴</th>
            <td
              :id="targetIdMap.profile.educationLevel"
              class="boss-table-data"
              data-test="education-level"
            >
              {{ matchingSetting.educationLevel }}以上
            </td>
          </tr>
          <tr v-if="isShowGraduate">
            <th>卒業年</th>
            <td
              :id="targetIdMap.profile.graduateYear"
              class="boss-table-data"
              data-test="graduate-year"
            >
              {{ matchingSetting.graduateYear.from | nullToZero }}年 ~ {{ matchingSetting.graduateYear.to | nullToZero }}年に卒業した会員
            </td>
          </tr>
          <tr
            v-if="matchingSetting.educationalSearchWord.value"
            data-test="educational-search"
          >
            <th rowspan="2">
              学校名・学部名
              <div
                class="notice"
                data-test="educational-search-count"
              >
                ※{{ matchingSetting.educationalSearchWord.value.length }}文字/{{ matchingSetting.educationalSearchWord.max }}文字
              </div>
            </th>
            <td class="boss-table-data">
              <div class="complement">
                「学校名」「学部／学科名」から、フリーワード検索をおこないます。
              </div>
              <div class="complement">
                キーワードはスペース区切りで複数指定できます。
              </div>
            </td>
          </tr>
          <tr v-if="matchingSetting.educationalSearchWord.value">
            <td
              :id="targetIdMap.profile.educationalSearchWord"
              class="boss-table-data pega-pre-wrap"
              data-test="educational-search-word"
              v-text="educationalSearchWord"
            />
          </tr>
          <tr v-if="isShowWorkStatus">
            <th rowspan="2">
              現在の就業状況
            </th>
            <td
              v-if="matchingSetting.userWorkStatus"
              :id="targetIdMap.profile.userWorkStatus"
              class="boss-table-data"
              data-test="user-work-status"
            >
              {{ matchingSetting.userWorkStatus }}
            </td>
          </tr>
          <tr>
            <td
              v-if="matchingSetting.usersIncomeRange.min || matchingSetting.usersIncomeRange.max"
              :id="targetIdMap.profile.usersIncomeRange"
              class="boss-table-data"
              data-test="users-income-range"
            >
              現在の年収 {{ matchingSetting.usersIncomeRange.min | nullToZero }}万円 ~ {{ matchingSetting.usersIncomeRange.max | nullToZero }}万円
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="isShowOccupationAndBusiness">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="experienceCommentHighlights"
          :is-show="isShowComment"
          data-test="experience-comments"
          v-on="$listeners"
        />
        <table
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-experience"
        >
          <thead>
            <tr>
              <th colspan="2">
                職種・業種経験
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="matchingSetting.jobExperienceList.length > 0">
              <th>経験職種</th>
              <td
                :id="targetIdMap.experience.jobExperienceList"
                class="boss-table-data pega-pre-wrap"
                data-test="jobExperienceList"
                v-text="jobExperienceList"
              />
            </tr>
            <tr v-if="matchingSetting.experiencedBusinesses.length > 0">
              <th>経験業種</th>
              <td
                :id="targetIdMap.experience.business"
                class="boss-table-data pega-pre-wrap"
                data-test="businesses"
                v-text="businesses"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="isShowLanguage">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="languageCommentHighlights"
          :is-show="isShowComment"
          data-test="language-comments"
          v-on="$listeners"
        />
        <table
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-language"
        >
          <thead>
            <tr>
              <th colspan="3">
                語学力
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th rowspan="2">
                英語力
              </th>
              <td
                class="boss-table-data"
                colspan="2"
              >
                <div
                  :id="targetIdMap.language.score"
                  class="pega-pre-wrap"
                  data-test="score"
                  v-text="languageScore"
                />
              </td>
            </tr>
            <tr v-if="matchingSetting.englishLevel">
              <th>
                英語スキル
              </th>
              <td
                :id="targetIdMap.language.englishLevel"
                class="boss-table-data"
                data-test="english-level"
              >
                {{ matchingSetting.englishLevel }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="matchingSetting.skills.length > 0">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="skillsCommentHighlights"
          :is-show="isShowComment"
          data-test="skill-comments"
          v-on="$listeners"
        />
        <table
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-skill"
        >
          <thead>
            <tr>
              <th colspan="2">
                スキル・経験
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(skill,key,index) in skillGroup"
              :key="key"
            >
              <th :data-test="`skill-header-${index}`">
                {{ key }}
              </th>
              <td
                :id="targetIdMap.skill[skill[0].group.id]"
                class="boss-table-data pega-pre-wrap"
                :data-test="`skill-text-${index}`"
                v-text="skill.text"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="matchingSetting.licenses.length > 0">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="licensesCommentHighlights"
          :is-show="isShowComment"
          data-test="license-comments"
          v-on="$listeners"
        />
        <table
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-license"
        >
          <thead>
            <tr>
              <th colspan="2">
                保有資格登録
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(license,key,index) in licenseGroup"
              :key="key"
            >
              <th :data-test="`license-header-${index}`">
                {{ key }}
              </th>
              <td
                :id="targetIdMap.license[license[0].path[0].id]"
                class="boss-table-data pega-pre-wrap"
                :data-test="`license-text-${index}`"
                v-text="license.text"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="matchingSetting.freeWord.value">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="freewordCommentHighlights"
          :is-show="isShowComment"
          data-test="free-word-comments"
          v-on="$listeners"
        />
        <table
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-free-word"
        >
          <thead>
            <tr>
              <th colspan="2">
                フリーワード
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th rowspan="2">
                フリーワード
                <div
                  class="notice"
                  data-test="free-word-count"
                >
                  ※{{ matchingSetting.freeWord.value.length }}文字/{{ matchingSetting.freeWord.max }}文字
                </div>
              </th>
              <td class="boss-table-data">
                <div class="complement">
                  「職務経歴書」「保有資格」「自己PR」から、フリーワード検索をおこないます。
                </div>
                <div class="complement">
                  キーワードはスペース区切りで複数指定できます。
                </div>
              </td>
            </tr>
            <tr>
              <td
                :id="targetIdMap.freeword.freeWord"
                class="boss-table-data pega-pre-wrap"
                data-test="free-word-text"
                v-text="freeWord"
              />
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="matchingSetting.registrationDate">
      <div class="pega-comment-block">
        <preview-job-comments
          :comments="registrationDateCommentHighlights"
          :is-show="isShowComment"
          data-test="registration-date-comments"
          v-on="$listeners"
        />
        <table
          v-if="matchingSetting.registrationDate"
          class="unstriped hover pega-table pega-preview-table"
          :class="isShowComment ? 'pega-cursor-pen-only-id' : ''"
          data-test="matching-setting-registration-date"
        >
          <thead>
            <tr>
              <th colspan="2">
                入会時期の指定
              </th>
            </tr>
          </thead>
          <tbody>
            <th>新規会員の指定</th>
            <td
              :id="targetIdMap.registrationDate"
              class="boss-table-data"
              data-test="registration-date"
            >
              {{ matchingSetting.registrationDate }}
            </td>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import PreviewJobComments from '@/components/molecules/PreviewJobComments'
import jobPreviewCommon from '@/lib/jobPreviewCommon'
import { mapActions, mapGetters } from 'vuex'
import { parseHierarchicalObjects } from '@/lib/parse'
import _ from 'lodash'

export default {
  components: {
    PreviewJobComments
  },
  filters: {
    nullToZero (val) {
      // 2024.7 リリース前後で表示差分が出ないように0で置換
      return val || 0
    },
    joinArray (val) {
      return val.join(' / ')
    }
  },
  mixins: [jobPreviewCommon],
  props: {
    matchingSetting: {
      type: Object,
      required: true
    },
    commentHighlights: {
      type: Array,
      default: () => []
    },
    isShowComment: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      isFinishCreated: false
    }
  },
  computed: {
    ...mapGetters([
      'businessesMaster',
      'skillsMaster',
      'licensesMaster'
    ]),
    profileCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.profile.prefix))
    },
    experienceCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.experience.prefix))
    },
    languageCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.language.prefix))
    },
    skillsCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.skill.prefix))
    },
    licensesCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.license.prefix))
    },
    freewordCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target.startsWith(this.targetIdMap.freeword.prefix))
    },
    registrationDateCommentHighlights () {
      return this.commentHighlights.filter(commentHighlight => commentHighlight.target === this.targetIdMap.registrationDate)
    },
    matchingSettingStatusLabel () {
      return this.matchingSetting.isStopMatching ? '停止中' : '条件あり'
    },
    isShowGraduate () {
      return this.matchingSetting.graduateYear.from || this.matchingSetting.graduateYear.to
    },
    isShowWorkStatus () {
      return this.matchingSetting.userWorkStatus ||
      this.matchingSetting.usersIncomeRange.min ||
      this.matchingSetting.usersIncomeRange.max
    },
    isShowOccupationAndBusiness () {
      return this.matchingSetting.jobExperienceList.length > 0 ||
      this.matchingSetting.experiencedBusinesses.length > 0
    },
    isShowLanguage () {
      return this.matchingSetting.toeicScore ||
      this.matchingSetting.toeflScore ||
      this.matchingSetting.englishLevel
    },
    languageScore () {
      const toeicLabel = this.matchingSetting.toeicScore ? `${this.matchingSetting.toeicScore} 点以上` : '指定なし'
      const toeflLabel = this.matchingSetting.toeflScore ? `${this.matchingSetting.toeflScore} 点以上` : '指定なし'
      return `TOEIC ${toeicLabel}\nTOEFL ${toeflLabel}`
    },
    educationalSearchWord () {
      return `${this.matchingSetting.educationalSearchWord.value}\n${this.operatingText(this.matchingSetting.isEducationOrSearch)}`
    },
    freeWord () {
      return `${this.matchingSetting.freeWord.value}\n${this.operatingText(this.matchingSetting.isFreeWordOrSearch)}`
    },
    jobExperienceList () {
      return this.createJobExperienceText(this.matchingSetting.jobExperienceList)
    },
    businesses () {
      return this.createExperienceText(
        this.businessesMaster,
        this.matchingSetting.experiencedBusinesses,
        0, 1
      )
    },
    skillGroup () {
      const skillIds = this.matchingSetting.skills.map(m => m.id)
      const skills = _.groupBy(
        parseHierarchicalObjects(this.skillsMaster)
          .filter(skillMaster => skillIds.includes(skillMaster.id))
          .map(m => {
            return {
              group: m.path[0],
              groupSecondary: m.path[1],
              name: m.path[2].name + ' / ' + m.path[3].name +
              '(' + this.matchingSetting.skills.find(skill => skill.id === m.id).yearsLabel + ')'
            }
          })
        , o => o.group.name)
      Object.keys(skills).map(key => {
        skills[key].text = []
        const nestedSkills = _.groupBy(skills[key], o => o.groupSecondary.name)
        Object.keys(nestedSkills).map(nestedKey => {
          skills[key].text.push(
            '■ ' + nestedKey + '\n' +
          nestedSkills[nestedKey].map(m => m.name).join('\n'))
        })
        skills[key].text = skills[key].text.join('\n\n')
      })
      return skills
    },
    licenseGroup () {
      const licenseIds = this.matchingSetting.licenses
      const licenses = _.groupBy(
        parseHierarchicalObjects(this.licensesMaster)
          .filter(licenseMaster => licenseIds.includes(licenseMaster.id))
        , o => o.path[0].name)
      Object.keys(licenses).map(key => {
        licenses[key].text = licenses[key].map(m => {
          return m.path[0].name + ' / ' + m.path[1].name
        }).join('\n')
      })
      return licenses
    }
  },
  watch: {
    async isFinishCreated () {
      await this.$nextTick()
      this.isFinishCreated && this.initCommentHighlights()
    }
  },
  created () {
    Promise.all([
      this.storeMaster('businesses'),
      this.storeMaster('skills'),
      this.storeMaster('licenses')
    ]).then(() => { this.isFinishCreated = true })
  },
  methods: {
    ...mapActions([
      'storeMaster'
    ]),
    initCommentHighlights () {
      this.profileCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.experienceCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.languageCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.skillsCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.licensesCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.freewordCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
      this.registrationDateCommentHighlights.forEach((commentHighlight) => {
        this.setHighlight(commentHighlight)
      })
    },
    operatingText (val) {
      if (val) {
        return '※いずれかのキーワードを含む（OR検索）'
      } else {
        return '※すべてのキーワードを含む（AND検索）'
      }
    },
    createExperienceText (master, experiences, firstPath, secondPath) {
      const parseMaster = parseHierarchicalObjects(master)
      return experiences.map(i => {
        const parseExperience = parseMaster.find(master => master.id === i)
        return '● 「' + parseExperience.path[firstPath].name + '」で「' +
        parseExperience.path[secondPath].name + '」の経験のある方'
      }).join('\n')
    },
    createJobExperienceText (jobExperienceLabelList) {
      return jobExperienceLabelList.map(label => label).join('\n')
    }
  }
}
</script>
